import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Loader2, ShoppingCart } from "lucide-react";
import api from "@/lib/api";
import { startCheckout } from "@/lib/checkout";
import { T } from "@/constants/testIds";
import { LineReveal, FadeUp, Eyebrow } from "@/components/motion";
import Seo from "@/components/Seo";
import TiltCover from "@/components/TiltCover";
import Marquee from "@/components/Marquee";
import NewsletterForm from "@/components/NewsletterForm";
import Trailer from "@/components/Trailer";

const MARQUEE_ITEMS = [
  "Metamorphosis — 2015",
  "Nano — 2019",
  "Transhuman — 2023",
  "SABA Book Awards · Third Place · London 2020",
  "Amazon Best-Selling Author",
  "Focus On Women Magazine · 2015",
  "Nova Scotia · Canada",
];

const MANIFESTO = [
  {
    numeral: "I",
    title: "The Nurse",
    body: "Twenty-two years as a registered nurse, with numerous specialties. Isabella built her life around care — first as a mother of two, then in the wards and clinics of Nova Scotia, where medicine was a language she spoke fluently.",
  },
  {
    numeral: "II",
    title: "The Patient",
    body: "Then the body turned the page. Isabella lives with Morgellons disease and chronic Lyme — an illness right out of the Twilight Zone. She has participated in research with the CEHF and speaks openly about targeted individuals and non-consensual bio-experimentation.",
  },
  {
    numeral: "III",
    title: "The Author",
    body: "What medicine couldn't cure, writing transformed. Metamorphosis (2015), Nano (2019), and Transhuman (2023) turned one woman's ordeal into a trilogy — earning third place at the SABA Book Awards in London in 2020 and best-selling status on Amazon.",
  },
];

function Hero({ book }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.15]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-center overflow-hidden">
      <div className="absolute inset-0 overflow-hidden" aria-hidden>
        <img
          src="/img/coast.jpg"
          alt=""
          className="h-full w-full scale-105 object-cover opacity-45"
          style={{ filter: "saturate(0.7) brightness(0.75)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070A11]/70 via-[#070A11]/55 to-[#070A11]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#070A11]/80 via-transparent to-[#070A11]/40" />
      </div>

      <motion.div
        style={{ opacity }}
        className="relative z-10 mx-auto grid w-full max-w-[1440px] grid-cols-1 items-center gap-14 px-5 pb-24 pt-32 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-14 lg:pb-16 lg:pt-24"
      >
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <Eyebrow className="mb-7">Author — Nova Scotia, Canada</Eyebrow>
          <h1 className="font-serif text-[13.5vw] font-light leading-[0.98] tracking-tight text-ink sm:text-6xl lg:text-[5.6rem] xl:text-[6.4rem]">
            <LineReveal
              lines={["She survived", "the experiment", "she never agreed to."]}
              delay={0.25}
              testid="hero-headline"
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-xl text-base font-light leading-relaxed text-dim sm:text-lg"
          >
            Isabella MacLeod — nurse, survivor, and author of{" "}
            <span className="serif-italic text-ink">The Miranda trilogy</span>:{" "}
            Metamorphosis, Nano, and Transhuman. A story drawn from an ordeal no one
            should have to call research.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/books"
              data-testid={T.heroCtaPrimary}
              className="group flex items-center gap-3 rounded-full bg-cyanx px-7 py-4 font-mono text-[11px] uppercase tracking-[0.24em] text-void transition-all duration-300 hover:brightness-110"
            >
              Discover the trilogy
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
            <Link
              to="/about"
              data-testid={T.heroCtaSecondary}
              className="group flex items-center gap-2 rounded-full border border-white/15 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.24em] text-ink transition-all duration-300 hover:border-cyanx/60 hover:text-cyanx"
            >
              About Isabella
            </Link>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yImg }} className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto w-[88%] max-w-[460px] lg:w-full"
          >
            <div className="relative w-full" style={{ aspectRatio: "1 / 1.05" }} data-testid="hero-cover-stack">
              <img
                src="/books/metamorphosis.jpg"
                alt="Metamorphosis — cover art"
                className="absolute left-0 top-2 w-[52%] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.8)] ring-1 ring-white/10"
                style={{ "--float-rot": "-9deg", animation: "floaty 9s ease-in-out infinite" }}
              />
              <img
                src="/books/nano.jpg"
                alt="Nano — cover art"
                className="absolute left-[26%] top-[26%] w-[50%] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.8)] ring-1 ring-white/10"
                style={{ "--float-rot": "3deg", animation: "floaty 11s ease-in-out infinite reverse" }}
              />
              <div className="absolute right-0 top-[8%] w-[58%] rotate-[2deg]">
                <TiltCover src="/books/transhuman.jpg" alt="Transhuman — cover art" />
              </div>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              {["R.N. → Patient → Author", "22 yrs nursing", "3 books"].map((chip) => (
                <span
                  key={chip}
                  className="rounded-full border border-white/10 bg-fog/80 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-dim"
                >
                  {chip}
                </span>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
        aria-hidden
      >
        <div className="h-10 w-[1px] overflow-hidden bg-white/15">
          <motion.div
            className="h-4 w-full bg-cyanx"
            animate={{ y: [0, 26, 0] }}
            transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
}

function FeaturedBook({ book }) {
  const [buying, setBuying] = useState(false);
  if (!book) return null;
  return (
    <section className="mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-14 lg:py-36">
      <div className="grid items-center gap-14 lg:grid-cols-12">
        <FadeUp className="lg:col-span-5" testid={T.featuredBook}>
          <div className="mx-auto max-w-[400px]">
            <TiltCover src={book.cover} alt={`${book.title} — cover art`} />
          </div>
        </FadeUp>
        <div className="lg:col-span-7 lg:pl-10">
          <FadeUp>
            <Eyebrow className="!text-ember mb-6">The Final Chapter — Out Now</Eyebrow>
            <h2 className="font-serif text-4xl font-light leading-tight tracking-tight text-ink sm:text-5xl lg:text-6xl">
              {book.title}
            </h2>
            <p className="mt-3 text-sm uppercase tracking-[0.2em] text-mute2">{book.subtitle}</p>
            <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-dim sm:text-lg">
              {book.synopsis.split("\n\n")[0]}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <span className="font-serif text-2xl italic text-cyanx">{book.price_label}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute2">
                {book.price_note}
              </span>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              {book.stripe_lookup_key && (
                <button
                  onClick={async () => {
                    try {
                      setBuying(true);
                      await startCheckout(book.stripe_lookup_key);
                    } catch {
                      setBuying(false);
                      toast.error("Could not start checkout. Please try again.");
                    }
                  }}
                  disabled={buying}
                  data-testid={`buy-direct-${book.slug}`}
                  className="group flex items-center gap-2 rounded-full bg-cyanx px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-void transition-all duration-300 hover:brightness-110 disabled:opacity-60"
                >
                  {buying ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <>
                      <ShoppingCart size={14} />
                      Buy direct — {book.direct_price_label}
                    </>
                  )}
                </button>
              )}
              <a
                href={book.amazon_com}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={T.buyAmazonCom(book.slug)}
                className="group flex items-center gap-2 rounded-full bg-ink px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-void transition-all duration-300 hover:bg-cyanx"
              >
                Buy on Amazon.com
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <a
                href={book.amazon_ca}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={T.buyAmazonCa(book.slug)}
                className="group flex items-center gap-2 rounded-full border border-white/15 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-all duration-300 hover:border-cyanx/60 hover:text-cyanx"
              >
                Amazon.ca
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <Link
                to={`/books/${book.slug}`}
                data-testid={T.bookLink(book.slug)}
                className="flex items-center gap-2 px-2 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-dim transition-colors hover:text-cyanx"
              >
                Read more
              </Link>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function TrilogySection({ books }) {
  return (
    <section className="border-t border-white/[0.07] bg-[#0E1422]/40">
      <div className="mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-14 lg:py-32" data-testid={T.trilogySection}>
        <FadeUp className="mb-16 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <Eyebrow className="mb-5">The Catalog</Eyebrow>
            <h2 className="font-serif text-4xl font-light tracking-tight text-ink sm:text-5xl">
              Three books. <span className="serif-italic text-cyanx">One ordeal.</span>
            </h2>
          </div>
          <Link
            to="/books"
            className="group flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-dim transition-colors hover:text-cyanx"
            data-testid="trilogy-view-all-link"
          >
            All editions &amp; formats
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>

        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {books.map((b, i) => (
            <FadeUp key={b.slug} delay={i * 0.12} testid={T.bookCard(b.slug)}>
              <Link to={`/books/${b.slug}`} className="group block" data-testid={T.bookLink(b.slug)}>
                <div className="relative mb-7 overflow-hidden">
                  <TiltCover src={b.cover} alt={`${b.title} — cover art`} glow={i === 1 ? "255,107,53" : "0,240,255"} />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-mute2">
                  {String(i + 1).padStart(2, "0")} — {b.year}
                </p>
                <h3 className="mt-2 font-serif text-3xl font-light text-ink transition-colors duration-300 group-hover:text-cyanx">
                  {b.title}
                </h3>
                <p className="mt-2 line-clamp-2 text-sm font-light leading-relaxed text-dim">{b.tagline}</p>
                <div className="mt-4 flex items-center justify-between border-t border-white/[0.07] pt-4">
                  <span className="font-mono text-[11px] tracking-[0.15em] text-cyanx">{b.price_label}</span>
                  <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-mute2 transition-colors group-hover:text-ink">
                    Details <ArrowUpRight size={12} />
                  </span>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-14 lg:py-36" data-testid={T.manifestoSection}>
      <FadeUp className="mb-20 max-w-2xl">
        <Eyebrow className="mb-5">The Story Behind the Story</Eyebrow>
        <h2 className="font-serif text-4xl font-light tracking-tight text-ink sm:text-5xl lg:text-6xl">
          Written from <span className="serif-italic text-cyanx">inside the wound.</span>
        </h2>
      </FadeUp>

      <div>
        {MANIFESTO.map((m, i) => (
          <FadeUp key={m.numeral} delay={0.05}>
            <div className="grid gap-6 border-t border-white/[0.07] py-12 md:grid-cols-12 md:gap-10">
              <div className="md:col-span-2">
                <span className="font-serif text-7xl font-light leading-none text-white/[0.14] sm:text-8xl">
                  {m.numeral}
                </span>
              </div>
              <div className="md:col-span-4">
                <h3 className="font-serif text-3xl font-light text-ink sm:text-4xl">{m.title}</h3>
              </div>
              <div className="md:col-span-6">
                <p className="max-w-xl text-base font-light leading-relaxed text-dim">{m.body}</p>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

function TrailerSection() {
  return (
    <section id="trailer" className="border-t border-white/[0.07] bg-[#0E1422]/40">
      <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-14 lg:py-32">
        <FadeUp className="lg:col-span-7">
          <Eyebrow className="mb-5">Watch</Eyebrow>
          <h2 className="font-serif text-4xl font-light leading-tight tracking-tight text-ink sm:text-5xl lg:text-6xl">
            The trilogy, <span className="serif-italic text-cyanx">in motion.</span>
          </h2>
          <p className="mt-7 max-w-lg text-base font-light leading-relaxed text-dim sm:text-lg">
            The official reel for the Miranda trilogy — three covers, one fight,
            and the fog of the Nova Scotian coast where it was written.
          </p>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-mute2">
            Press play — sound on for the full effect
          </p>
        </FadeUp>
        <div className="lg:col-span-5">
          <FadeUp delay={0.1}>
            <Trailer testid="trailer-video" />
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function JoinSection() {
  return (
    <section className="relative overflow-hidden border-t border-white/[0.07]">
      <div className="absolute inset-0" aria-hidden>
        <img src="/img/coast2.jpg" alt="" className="h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070A11] via-[#070A11]/70 to-[#070A11]" />
      </div>
      <div className="relative mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-14 lg:py-36">
        <div className="mx-auto max-w-2xl text-center">
          <FadeUp>
            <Eyebrow className="mb-6 justify-center">The Reader List</Eyebrow>
            <h2 className="font-serif text-4xl font-light tracking-tight text-ink sm:text-5xl lg:text-6xl">
              Letters from <span className="serif-italic text-cyanx">the fog</span>
            </h2>
            <p className="mx-auto mt-6 max-w-lg text-base font-light leading-relaxed text-dim">
              Book news, essays, and behind-the-book notes — straight from Nova Scotia.
              Confirm by email to join; leave any time with one click.
            </p>
            <div className="mt-10 text-left">
              <NewsletterForm />
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { data: books } = useQuery({
    queryKey: ["books"],
    queryFn: async () => (await api.get("/books")).data,
  });
  const featured = books?.find((b) => b.slug === "transhuman");

  return (
    <>
      <Seo
        title="Isabella MacLeod — Author of the Miranda Trilogy"
        description="Metamorphosis, Nano, and Transhuman — the Miranda trilogy by Canadian author Isabella MacLeod. Nurse, survivor, storyteller. Buy on Amazon or direct."
        path="/"
      />
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />
      <FeaturedBook book={featured} />
      <TrilogySection books={books || []} />
      <Manifesto />
      <TrailerSection />
      <JoinSection />
    </>
  );
}
